import { createSelector } from 'reselect';
import { RootState, AlertState, ListViewRow } from '../model/state';
import { Location, Alert, AlertStatus, isLocationFullAccess } from 'model';

/**
 * Direct selector to the state
 */
const selectAlert = () => (state: RootState) => state.alert;

/**
 * Other specific selectors
 */

const selectAlerts = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.list
);

const selectIsAllSelected = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.isAllSelected
);

const selectAlertSearch = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.search
);

const selectAlertTotals = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.totals
);

const selectCurrentPage = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.list.page
);

const selectHasMoreAlerts = () => createSelector(
  selectAlert(),
  (alert: AlertState) => (alert.list.total || 0) > (alert.list.rows?.length || 0)
);

const selectCurrentAlert = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.currentAlert
);

const selectIsSubmittingAlertAction = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.isSubmittingAction
);

const selectIsSelectedAnyAlert = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.list.rows?.find(alert => alert.isSelected) !== undefined
);

const selectSelectedAlertNumber = () => createSelector(
  selectAlert(),
  (alert: AlertState) => (alert.list.rows?.filter(alert => alert.isSelected) || []).length
);

const selectSelectedAlerts = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.list.rows?.filter(loc => loc.isSelected) || []
);

const selectSelectedAlertsToResolve = () => createSelector(
  selectSelectedAlerts(),
  (alerts: Array<ListViewRow<Alert>>) => alerts.filter(alert => alert.status === AlertStatus.TRIGGERED)
);

const selectSelectedAlertsToResolveNumber = () => createSelector(
  selectSelectedAlertsToResolve(),
  (alerts: Array<ListViewRow<Alert>>) => alerts.length
);

const selectIsBulkResolveActionEnabled = () => createSelector(
  selectSelectedAlertsToResolve(),
  (alerts: Array<ListViewRow<Alert>>) => {
    const hasOneReadOnly = alerts.find(alert => {
      return (alert.location as Location).roles ? !isLocationFullAccess(alert.location as Location) : false;
    });
    return hasOneReadOnly === undefined;
  }
);

const selectAlertFilters = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.filters
);

const selectAlarmList = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.alarmList
);

const selectAlertExportCsvData = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.exportCsvData
);

const selectFetchingCsvData = () => createSelector(
  selectAlert(),
  (alert: AlertState) => alert.fetchingCsvData
);

export default selectAlert;
export {
  selectAlerts,
  selectIsAllSelected,
  selectAlertSearch,
  selectAlertTotals,
  selectCurrentPage,
  selectHasMoreAlerts,
  selectCurrentAlert,
  selectIsSubmittingAlertAction,
  selectIsSelectedAnyAlert,
  selectSelectedAlertNumber,
  selectSelectedAlertsToResolve,
  selectSelectedAlertsToResolveNumber,
  selectIsBulkResolveActionEnabled,
  selectAlertFilters,
  selectAlarmList,
  selectAlertExportCsvData,
  selectFetchingCsvData,
}
